/**
 * Centralized date formatting utilities
 * Provides consistent date/time formatting across the app
 */

/**
 * Format a date string to a readable date
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDate(dateString, options = {}) {
  if (!dateString) return ''

  let date
  if (dateString instanceof Date) {
    date = dateString
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Date-only string: parse as local date to avoid timezone shift
    const [y, m, d] = dateString.split('-').map(Number)
    date = new Date(y, m - 1, d)
  } else {
    date = new Date(dateString)
  }
  if (isNaN(date.getTime())) return ''

  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }

  return date.toLocaleDateString(undefined, defaultOptions)
}

/**
 * Format a date string to a readable time
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {boolean} includeSeconds - Whether to include seconds
 * @returns {string} Formatted time string
 */
export function formatTime(dateString, includeSeconds = false) {
  if (!dateString) return ''

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return ''

  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }

  if (includeSeconds) {
    options.second = '2-digit'
  }

  return date.toLocaleTimeString(undefined, options)
}

/**
 * Format a date string to include both date and time
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(dateString) {
  if (!dateString) return ''

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return ''

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday")
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Relative time string
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return ''

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffSecs < 60) {
    return 'just now'
  } else if (diffMins < 60) {
    return `${diffMins}m ago`
  } else if (diffHours < 24) {
    return `${diffHours}h ago`
  } else if (diffDays === 1) {
    return 'yesterday'
  } else if (diffDays < 7) {
    return `${diffDays}d ago`
  } else if (diffWeeks < 4) {
    return `${diffWeeks}w ago`
  } else if (diffMonths < 12) {
    return `${diffMonths}mo ago`
  } else {
    return formatDate(date)
  }
}

/**
 * Format a date for chat message grouping (Today, Yesterday, or date)
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Chat-friendly date string
 */
export function formatChatDate(dateString) {
  if (!dateString) return ''

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (dateOnly.getTime() === today.getTime()) {
    return 'Today'
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday'
  } else {
    return formatDate(date)
  }
}

/**
 * Format a date for API requests (ISO format)
 * @param {Date} date - Date object
 * @returns {string} ISO date string
 */
export function toISODate(date) {
  if (!date || !(date instanceof Date)) return ''
  return date.toISOString().split('T')[0]
}

/**
 * Format a date and time for API requests (ISO format)
 * @param {Date} date - Date object
 * @returns {string} ISO datetime string
 */
export function toISODateTime(date) {
  if (!date || !(date instanceof Date)) return ''
  return date.toISOString()
}

/**
 * Check if a date is today
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {boolean}
 */
export function isToday(dateString) {
  if (!dateString) return false

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return false

  const now = new Date()
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  )
}

/**
 * Check if a date is in the past
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {boolean}
 */
export function isPast(dateString) {
  if (!dateString) return false

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return false

  return date < new Date()
}

/**
 * Check if a date is in the future
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {boolean}
 */
export function isFuture(dateString) {
  if (!dateString) return false

  const date = dateString instanceof Date ? dateString : new Date(dateString)
  if (isNaN(date.getTime())) return false

  return date > new Date()
}

/**
 * Get the start of day for a date
 * @param {Date} date - Date object
 * @returns {Date}
 */
export function startOfDay(date = new Date()) {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

/**
 * Get the end of day for a date
 * @param {Date} date - Date object
 * @returns {Date}
 */
export function endOfDay(date = new Date()) {
  const result = new Date(date)
  result.setHours(23, 59, 59, 999)
  return result
}

/**
 * Add days to a date
 * @param {Date} date - Date object
 * @param {number} days - Number of days to add
 * @returns {Date}
 */
export function addDays(date, days) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Format duration in minutes to human readable
 * @param {number} minutes - Duration in minutes
 * @returns {string}
 */
export function formatDuration(minutes) {
  if (!minutes || minutes < 0) return ''

  if (minutes < 60) {
    return `${minutes}min`
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (mins === 0) {
    return `${hours}h`
  }

  return `${hours}h ${mins}min`
}

/**
 * Get greeting based on time of day
 * @returns {string} "Good morning", "Good afternoon", or "Good evening"
 */
export function getGreeting() {
  const hour = new Date().getHours()

  if (hour < 12) {
    return 'Good morning'
  } else if (hour < 17) {
    return 'Good afternoon'
  } else {
    return 'Good evening'
  }
}
